// Laptops and desktops: a navy sidebar with every section one click away and
// live counts for what needs doing. Phones keep the header and bottom bar.
import { ChatCircleText, MagnifyingGlass } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { api, type Me } from "../lib/api.ts";
import { nextQuery } from "../lib/next.ts";
import { Mark } from "./brand.tsx";
import { openFeedback } from "./feedback-button.tsx";
import { AgencyPass } from "./header.tsx";
import { HelpButton } from "./help.tsx";
import { openQuickFind } from "./quick-find.tsx";
import { sectionsFor } from "./sections.ts";
import { tasksQuery } from "./tasks.tsx";

const shortcut = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "\u2318K" : "Ctrl K";

export function Sidebar({ me }: { me: Me }) {
	const can = (p?: string) => !p || me.permissions.includes(p);
	const tasks = useQuery({ ...tasksQuery({}), enabled: can("tasks.manage") });
	const queue = useQuery({
		queryKey: ["compliance-queue"],
		queryFn: () => api<unknown[]>("/compliance/queue"),
		enabled: can("compliance.verify"),
	});
	// The Waiting for you count waits until the page has loaded what it needs first.
	const [idle, setIdle] = useState(false);
	useEffect(() => {
		const t = setTimeout(() => setIdle(true), 2500);
		return () => clearTimeout(t);
	}, []);
	const next = useQuery({ ...nextQuery, enabled: can("tasks.manage") && idle, staleTime: 60_000 });
	// Tasks counts each kind of thing waiting once, not every timesheet in it.
	const counts = {
		tasks: (tasks.data?.length ?? 0) + (next.data?.items.length ?? 0),
		queue: queue.data?.length ?? 0,
	};
	const groups = sectionsFor(me);
	// On a short laptop screen the list scrolls: keep the page you're on in view.
	const nav = useRef<HTMLElement>(null);
	const path = useRouterState({ select: (st) => st.location.pathname });
	// biome-ignore lint/correctness/useExhaustiveDependencies: runs when the page changes
	useEffect(() => {
		nav.current?.querySelector('[data-status="active"]')?.scrollIntoView({ block: "nearest" });
	}, [path]);

	return (
		<aside className="on-navy sticky top-0 hidden h-dvh w-64 shrink-0 flex-col bg-navy text-white shadow-[inset_-4px_0_0_var(--color-pink)] lg:flex">
			<div className="flex items-center gap-3 px-5 pt-4 pb-3">
				<Link to="/" aria-label="Home" className="rounded-md text-mint">
					<Mark size={36} />
				</Link>
			</div>
			<div className="px-4">
				<AgencyPass me={me} inline />
			</div>
			{/* Find and Help share a row, so more of the menu fits on a laptop screen. */}
			<div className="flex gap-2 px-3 pt-3">
				<button
					type="button"
					onClick={openQuickFind}
					aria-keyshortcuts={shortcut === "Ctrl K" ? "Control+K" : "Meta+K"}
					className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-xl border border-white/25 px-3 text-left font-semibold text-white/85 hover:bg-white/10"
				>
					<MagnifyingGlass size={20} weight="bold" aria-hidden="true" className="shrink-0 text-mint" />
					<span className="flex-1">Find</span>
					<kbd className="rounded border border-white/30 px-1.5 text-xs text-white/75">{shortcut}</kbd>
				</button>
				<div className="shrink-0">
					<HelpButton look="sidebar" />
				</div>
			</div>
			<nav ref={nav} aria-label="Main" className="navy-scroll mt-3 flex-1 overflow-y-auto px-3 py-1">
				{groups.map((group, i) => (
					<div key={group.label} className={i > 0 ? "mt-3" : ""}>
						{/* Section names, the same as the label above each page's title. */}
						<p id={`nav-group-${i}`} className="px-3 pb-1 text-sm font-bold tracking-[0.06em] text-white/70 uppercase">
							{group.label}
						</p>
						<ul aria-labelledby={`nav-group-${i}`} className="space-y-1">
							{group.items.map(({ to, label, Icon, exact, count }) => (
								<li key={to}>
									<Link
										to={to}
										activeOptions={{ exact: Boolean(exact) }}
										className="group flex min-h-11 items-center gap-3 rounded-xl px-3 font-semibold text-white/85 hover:bg-white/10 hover:text-white data-[status=active]:bg-white/12 data-[status=active]:text-white data-[status=active]:shadow-[inset_4px_0_0_var(--color-pink)]"
									>
										<Icon
											size={22}
											weight="fill"
											aria-hidden="true"
											className="shrink-0 text-mint group-data-[status=active]:text-pink"
										/>
										<span className="min-w-0 flex-1 truncate">{label}</span>
										{count && counts[count] > 0 ? (
											<span className="rounded-full bg-mint px-2 text-sm font-bold text-navy tabular-nums">
												{counts[count]}
												<span className="sr-only"> {count === "tasks" ? "to do" : "waiting"}</span>
											</span>
										) : null}
									</Link>
								</li>
							))}
						</ul>
					</div>
				))}
			</nav>
			<div className="border-t border-white/15 px-3 py-2">
				<button
					type="button"
					onClick={() => openFeedback()}
					className="flex min-h-11 w-full items-center gap-3 rounded-xl px-3 text-left font-semibold text-white/85 hover:bg-white/10 hover:text-white"
				>
					<ChatCircleText size={20} aria-hidden="true" className="shrink-0 text-mint" />
					Send feedback
				</button>
			</div>
			<Link to="/account" className="flex items-center gap-3 border-t border-white/15 px-5 py-3 hover:bg-white/10">
				<span className="inline-flex size-10 shrink-0 items-center justify-center rounded-full border-2 border-mint font-display font-bold text-mint">
					{me.user.name.trim().charAt(0).toUpperCase()}
				</span>
				<span className="min-w-0">
					<span className="block truncate font-semibold">{me.user.name}</span>
					<span className="block truncate text-sm text-white/75">Your account</span>
				</span>
			</Link>
		</aside>
	);
}
